
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import "../Style/FirmwareOverAir.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.Error || data.msg || "API Error");
  return data;
};

const useAvailableVehiclesQuery = () => {
  return useQuery({
    queryKey: ["availableVehicles"],
    queryFn: async () => {
      const data = await fetchWithAuth("/vehicle/device/assign");
      return data.Vehicles || [];
    },
    staleTime: 30000,
  });
};

const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    style={{ width: "20px", height: "20px" }}
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);

const StatusBadge = ({ status }) => {
  let statusClassName = "badge-Firmware";
  const s = status?.toLowerCase();
  if (s === "completed") statusClassName += " badge-completed-Firmware";
  else if (s === "active" || s === "in_progress")
    statusClassName += " badge-active-Firmware";
  else if (s === "failed") statusClassName += " badge-failed-Firmware";
  else statusClassName += " badge-pending-approval-Firmware";

  return <span className={statusClassName}>{status}</span>;
};

// --- Create Form Component ---
const CreateRolloutForm = ({ onBackToList, retryData }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deviceType, setDeviceType] = useState(
    retryData?.Type === "LCT001" ? "LCT" : "TCU",
  );

  const [formData, setFormData] = useState({
    rolloutName: retryData?.firmware_name || "",
    deviceGroup: retryData?.deviceGroup || "",
    url: "",
    customerId: retryData?.customer || "",
    version: retryData?.version || "",
    crc32: retryData?.crc32 || "",
    location: retryData?.location || "",
    type: retryData?.Type || "",
  });

  const [vehicleInput, setVehicleInput] = useState("");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const [customerInput, setCustomerInput] = useState(retryData?.customer || "");
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const customerRef = useRef(null);
  const { data: vehicles = [], isLoading: fetchingVehicles } =
    useAvailableVehiclesQuery();

  const uniqueCustomers = useMemo(() => {
    const customers = vehicles.map((v) => String(v.Customerid)).filter(Boolean);
    return [...new Set(customers)];
  }, [vehicles]);

  const filteredCustomers = useMemo(() => {
    return uniqueCustomers.filter((c) =>
      c.toLowerCase().includes(customerInput.toLowerCase()),
    );
  }, [customerInput, uniqueCustomers]);

  useEffect(() => {
    if (!vehicles) return;
    const lowerSearch = vehicleInput.toLowerCase();
    const currentCustomer = formData.customerId.trim().toLowerCase();

    const filtered = vehicles.filter((v) => {
      const matchesSearch =
        v.VehicleID?.toLowerCase().includes(lowerSearch) ||
        v.DeviceID?.toLowerCase().includes(lowerSearch);
      const matchesCustomer = currentCustomer
        ? String(v.Customerid).toLowerCase() === currentCustomer
        : false;
      return matchesSearch && matchesCustomer;
    });
    setFilteredSuggestions(filtered);
  }, [vehicleInput, vehicles, formData.customerId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      )
        setShowSuggestions(false);
      if (customerRef.current && !customerRef.current.contains(event.target))
        setShowCustomerSuggestions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCustomerSelect = (custId) => {
    setFormData((prev) => ({ ...prev, customerId: custId }));
    setCustomerInput(custId);
    setShowCustomerSuggestions(false);
    setSelectedDevices([]);
  };

  const handleVehicleSelect = (vehicleObj) => {
    if (!selectedDevices.some((d) => d.DeviceID === vehicleObj.DeviceID)) {
      setSelectedDevices((prev) => [...prev, vehicleObj]);
    }
    setVehicleInput("");
    setShowSuggestions(false);
  };

  const removeDevice = (deviceId) =>
    setSelectedDevices((prev) => prev.filter((d) => d.DeviceID !== deviceId));

  const handleInputChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const isLCT = deviceType === "LCT";
      const createEndpoint = isLCT ? "/fota/LCT001/create" : "/fota/create";
      const triggerEndpointBase = isLCT
        ? "/fota/LCT001/trigger"
        : "/fota/trigger";

      if (retryData) {
        return await fetchWithAuth(`${triggerEndpointBase}/${retryData.id}`, {
          method: "POST",
          body: JSON.stringify({ url: formData.url }),
        });
      }

      const createRes = await fetchWithAuth(createEndpoint, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (createRes.fota_id) {
        await fetchWithAuth(`${triggerEndpointBase}/${createRes.fota_id}`, {
          method: "POST",
          body: JSON.stringify({ url: formData.url }),
        });
        return createRes.fota_id;
      }
      throw new Error("Creation failed");
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries(["fotaList"]);
      const finalId = retryData ? retryData.id : id;
      navigate(`/DeviceFotaDeploymentStatus/${finalId}`);
      onBackToList();
    },
    onError: (err) => alert(`Error: ${err.message}`),
  });

  const handleSubmit = () => {
    if (!formData.url) return alert("URL is required");
    if (!retryData && selectedDevices.length === 0)
      return alert("Select at least one vehicle");

    let payload;
    if (deviceType === "LCT") {
      payload = {
        customer_id: formData.customerId,
        device_group: formData.deviceGroup,
        firmware_name: formData.rolloutName,
        version: formData.version,
        type: "LCT001",
        devices: selectedDevices.map((d) => ({ device_id: d.DeviceID })),
      };
    } else {
      payload = {
        customer_id: formData.customerId,
        device_group: formData.deviceGroup,
        firmware_name: formData.rolloutName,
        version: formData.version,
        type: formData.type,
        crc32: formData.crc32,
        location: formData.location,
        devices: selectedDevices.map((d) => ({ device_id: d.DeviceID })),
      };
    }

    createMutation.mutate(payload);
  };

  return (
    <div className="Rollout-Firmware">
      <div className="Form-container-Firmware">
        <div className="Form-header-row-Firmware">
          <h1 className="Form-header-Firmware">
            {retryData
              ? `Restart Rollout: ${retryData.deviceGroup}`
              : "Create New Rollout"}
          </h1>
          <button onClick={onBackToList} className="back-button-Firmware">
            ← Back
          </button>
        </div>

        <div className="device-type-selector-Firmware">
          <label className="device-radio-label-Firmware">
            <input
              type="radio"
              name="deviceType"
              value="TCU"
              checked={deviceType === "TCU"}
              onChange={() => setDeviceType("TCU")}
              disabled={!!retryData}
            />
            <span>TCU</span>
          </label>
          <label className="device-radio-label-Firmware">
            <input
              type="radio"
              name="deviceType"
              value="LCT"
              checked={deviceType === "LCT"}
              onChange={() => setDeviceType("LCT")}
              disabled={!!retryData}
            />
            <span>LCT</span>
          </label>
        </div>

        <div className="Form-section-Firmware">
          <h2 className="Form-section-title-Firmware">
            {retryData ? "Provide New URL" : "Firmware Details"}
          </h2>
          <div className="Form-card-Firmware Form-grid-Firmware">
            {retryData ? (
              <div
                className="Form-field-Firmware"
                style={{ gridColumn: "1 / -1" }}
              >
                <label className="Form-label-Firmware">
                  * New Firmware URL
                </label>
                <input
                  name="url"
                  className="Form-input-Firmware"
                  value={formData.url}
                  onChange={handleInputChange}
                  placeholder="http://..."
                />
              </div>
            ) : (
              <>
                <div className="Form-field-Firmware">
                  <label className="Form-label-Firmware">* Firmware Name</label>
                  <input
                    name="rolloutName"
                    className="Form-input-Firmware"
                    value={formData.rolloutName}
                    onChange={handleInputChange}
                    placeholder="Firmware Name"
                  />
                </div>
                <div className="Form-field-Firmware">
                  <label className="Form-label-Firmware">* Device Group</label>
                  <input
                    name="deviceGroup"
                    className="Form-input-Firmware"
                    value={formData.deviceGroup}
                    onChange={handleInputChange}
                    placeholder="Device Group"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {!retryData && (
          <>
            <div className="Form-section-Firmware">
              <h2 className="Form-section-title-Firmware">Firmware Link</h2>
              <div className="Form-card-Firmware Form-grid-Firmware">
                <div
                  className="Form-field-Firmware"
                  style={deviceType === "LCT" ? { gridColumn: "1 / -1" } : {}}
                >
                  <label className="Form-label-Firmware">* URL</label>
                  <input
                    name="url"
                    className="Form-input-Firmware"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder="http://..."
                  />
                </div>

                {deviceType === "TCU" && (
                  <div className="Form-field-Firmware">
                    <label className="Form-label-Firmware">* Location</label>
                    <select
                      name="location"
                      className="Form-input-Firmware"
                      value={formData.location}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Location</option>
                      <option value="Application 1">Application 1</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="Form-section-Firmware">
              <h2 className="Form-section-title-Firmware">Vehicle Info</h2>
              <div className="Form-card-Firmware Form-grid-Firmware">
                <div
                  className="Form-field-Firmware"
                  ref={customerRef}
                  style={{ position: "relative" }}
                >
                  <label className="Form-label-Firmware">* Customer ID</label>
                  <input
                    type="text"
                    className="Form-input-Firmware"
                    placeholder="Select or search Customer..."
                    value={customerInput}
                    onChange={(e) => setCustomerInput(e.target.value)}
                    onFocus={() => setShowCustomerSuggestions(true)}
                  />
                  {showCustomerSuggestions && (
                    <ul
                      className="firmware-suggestions-list-Firmware"
                      style={{
                        position: "absolute",
                        zIndex: 100,
                        background: "#fff",
                        width: "100%",
                        border: "1px solid #ddd",
                        maxHeight: "150px",
                        overflowY: "auto",
                      }}
                    >
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((id, idx) => (
                          <li
                            key={idx}
                            className="suggestion-item-Firmware"
                            style={{ padding: "8px", cursor: "pointer" }}
                            onClick={() => handleCustomerSelect(id)}
                          >
                            {id}
                          </li>
                        ))
                      ) : (
                        <li
                          className="suggestion-item-Firmware"
                          style={{ padding: "8px" }}
                        >
                          No Customers Found
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                <div className="Form-field-Firmware">
                  <label className="Form-label-Firmware">* Version</label>
                  <input
                    name="version"
                    className="Form-input-Firmware"
                    value={formData.version}
                    onChange={handleInputChange}
                    placeholder="Enter Version"
                  />
                </div>

                {deviceType === "TCU" && (
                  <>
                    <div className="Form-field-Firmware">
                      <label className="Form-label-Firmware">* CRC32</label>
                      <input
                        name="crc32"
                        className="Form-input-Firmware"
                        value={formData.crc32}
                        onChange={handleInputChange}
                        placeholder="Enter CRC32"
                      />
                    </div>
                    <div className="Form-field-Firmware">
                      <label className="Form-label-Firmware">* Type</label>
                      <select
                        name="type"
                        className="Form-input-Firmware"
                        value={formData.type}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Type</option>
                        <option value="Application Code">
                          Application Code
                        </option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="Form-section-Firmware">
              <h2 className="Form-section-title-Firmware">
                Vehicle Selection ({selectedDevices.length} selected)
              </h2>
              <div className="Form-card-Firmware">
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "10px",
                  }}
                >
                  {selectedDevices.map((device) => (
                    <div
                      key={device.DeviceID}
                      style={{
                        background: "#e0f2fe",
                        color: "#0369a1",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #bae6fd",
                      }}
                    >
                      {device.VehicleID}{" "}
                      <span
                        onClick={() => removeDevice(device.DeviceID)}
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          color: "#ef4444",
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  className="Form-field-Firmware field-width-50-Firmware"
                  ref={searchInputRef}
                  style={{ position: "relative" }}
                >
                  <label className="Form-label-Firmware">
                    * Search Vehicle
                  </label>
                  <input
                    type="text"
                    className="Form-input-Firmware search-icon-bg"
                    placeholder={
                      formData.customerId
                        ? "Search vehicles..."
                        : "Select Customer ID first"
                    }
                    value={vehicleInput}
                    onChange={(e) => setVehicleInput(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    disabled={!formData.customerId}
                    style={{
                      backgroundColor: !formData.customerId
                        ? "#f1f5f9"
                        : "#fff",
                    }}
                  />
                  {showSuggestions && (
                    <ul
                      className="firmware-suggestions-list-Firmware"
                      style={{
                        position: "absolute",
                        zIndex: 90,
                        background: "#fff",
                        width: "100%",
                        border: "1px solid #ddd",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {fetchingVehicles ? (
                        <li
                          className="suggestion-item-Firmware"
                          style={{ padding: "8px" }}
                        >
                          Loading...
                        </li>
                      ) : filteredSuggestions.length > 0 ? (
                        filteredSuggestions.map((v, idx) => (
                          <li
                            key={idx}
                            className="suggestion-item-Firmware"
                            style={{ padding: "8px", cursor: "pointer" }}
                            onClick={() => handleVehicleSelect(v)}
                          >
                            {v.VehicleID} <small>({v.DeviceID})</small>
                          </li>
                        ))
                      ) : (
                        <li
                          className="suggestion-item-Firmware"
                          style={{ padding: "8px" }}
                        >
                          No vehicles found for this client
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="Form-actions-Firmware">
          <button
            className="Form-button-secondary-Firmware"
            onClick={onBackToList}
          >
            Cancel
          </button>
          <button
            className="Form-button-primary-Firmware"
            onClick={handleSubmit}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading
              ? "Processing..."
              : retryData
                ? "Restart Rollout"
                : "Start Rollout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function FirmwareOverAir() {
  const [currentPage, setCurrentPage] = useState("list");
  const [retryTarget, setRetryTarget] = useState(null);

  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["fotaList", page],

    queryFn: async () => {
      return await fetchWithAuth(`/fota?page=${page}&per_page=${perPage}`);
    },

    refetchInterval: 10000,
  });

  const rollouts = data?.data || [];
  const pagination = data?.pagination || {};

  const handleCreateNew = () => {
    setRetryTarget(null);
    setCurrentPage("create");
  };
  const handleRetry = (row) => {
    setRetryTarget(row);
    setCurrentPage("create");
  };

  return (
    <div>
      {currentPage === "create" ? (
        <CreateRolloutForm
          retryData={retryTarget}
          onBackToList={() => setCurrentPage("list")}
        />
      ) : (
        <div className="container-Firmware">
          <div className="card-Firmware">
            <div className="header-Firmware">
              <h1 className="title-Firmware">Deployments</h1>
              <p className="subtitle-Firmware">
                Manage and monitor your firmware rollouts.
              </p>
              <hr className="line-Firmware" />
            </div>
            <div className="action-bar-Firmware">
              <button className="button-Firmware" onClick={handleCreateNew}>
                <PlusIcon />
                New Device Firmware
              </button>
            </div>
            <div className="table-container-Firmware">
              {isLoading ? (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  Loading...
                </div>
              ) : (
                <>
                  <table className="table-Firmware">
                    <thead>
                      <tr>
                        <th className="th-Firmware">DEVICE GROUP</th>
                        <th className="th-Firmware">TYPE</th>
                        <th className="th-Firmware">VERSION</th>
                        <th className="th-Firmware">CUSTOMER</th>
                        <th className="th-Firmware">TOTAL</th>
                        <th className="th-Firmware">SUCCESS</th>
                        <th className="th-Firmware">FAILED</th>
                        <th className="th-Firmware">STATUS</th>
                        <th className="th-Firmware">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rollouts.map((row) => (
                        <tr key={row.id} className="tr-Firmware">
                          <td className="td-Firmware">{row.deviceGroup}</td>
                          <td className="td-Firmware">
                            {row.Type === "LCT001" ? row.Type : "TC1001"}
                          </td>
                          <td className="td-Firmware">{row.version}</td>
                          <td className="td-Firmware">{row.customer}</td>
                          <td className="td-Firmware">{row.deviceCount}</td>
                          <td className="td-Firmware">{row.successDevice}</td>
                          <td className="td-Firmware">{row.failedDevice}</td>
                          <td className="td-Firmware">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="td-Firmware">
                            {row.status?.toLowerCase() === "failed" ? (
                              <button
                                onClick={() => handleRetry(row)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#d48f0e",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                              >
                                Retry
                              </button>
                            ) : (
                              <Link
                                to={`/DeviceFotaDeploymentStatus/${row.id}`}
                                className="view-progress-link-Firmware"
                              >
                                View Progress
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="pagination-Firmware">
                    <button
                      className="pagination-btn-Firmware"
                      onClick={() => setPage((prev) => prev - 1)}
                      disabled={!pagination.has_prev}
                    >
                      Previous
                    </button>

                    <span className="pagination-info-Firmware">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>

                    <button
                      className="pagination-btn-Firmware"
                      onClick={() => setPage((prev) => prev + 1)}
                      disabled={!pagination.has_next}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
